import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe, Loader2, Send, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import { submitLead } from "@/lib/api";
import { useSettings } from "@/lib/SiteContent";
import SilvaCityProjectMap from "@/components/silva/SilvaCityProjectMap";
import SEO from "@/components/common/SEO";
import { getPageSeo } from "@/lib/seo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PROJECTS = [
  "Silva City",
  "Commercial Spaces",
  "Premium NA Plotting",
  "Other",
];

const ContactForm = () => {
  const [form, setForm] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    projectOfInterest: "Silva City",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) e.fullName = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress)) e.emailAddress = "Please enter a valid email address.";
    const digits = (form.phoneNumber || "").replace(/\D/g, "");
    if (digits.length < 7) e.phoneNumber = "Please enter a valid phone number.";
    if (!form.projectOfInterest) e.projectOfInterest = "Please choose a project.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitLead({
        ...form,
        message: form.message || undefined,
        source: "contact_form",
      });
      toast.success("Your enquiry has been received.", {
        description: "Our advisory team will reach out within 24 business hours.",
      });
      setSubmitted(true);
      setForm({ fullName: "", emailAddress: "", phoneNumber: "", projectOfInterest: "Silva City", message: "" });
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed.", {
        description: typeof detail === "string" ? detail : "Please verify your details and retry.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="glass-card p-7 md:p-10 backdrop-blur-xl">
      <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
        <span className="gold-divider" /> Send us an enquiry
      </p>
      <h3 className="font-display text-3xl md:text-4xl mt-3 leading-[1.1]">
        Tell us a little about your goals.
      </h3>

      <div className="mt-7 grid sm:grid-cols-2 gap-5">
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Full Name *</label>
          <input
            data-testid="contact-form-name-input"
            type="text"
            value={form.fullName}
            onChange={onChange("fullName")}
            className="input-luxe mt-2 w-full px-4 py-3 text-sm"
            placeholder="e.g. Priya Sharma"
            autoComplete="name"
          />
          {errors.fullName && <p className="mt-1 text-xs text-red-400">{errors.fullName}</p>}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Phone *</label>
          <input
            data-testid="contact-form-phone-input"
            type="tel"
            value={form.phoneNumber}
            onChange={onChange("phoneNumber")}
            className="input-luxe mt-2 w-full px-4 py-3 text-sm"
            placeholder="+91 98765 43210"
            autoComplete="tel"
          />
          {errors.phoneNumber && <p className="mt-1 text-xs text-red-400">{errors.phoneNumber}</p>}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Email *</label>
          <input
            data-testid="contact-form-email-input"
            type="email"
            value={form.emailAddress}
            onChange={onChange("emailAddress")}
            className="input-luxe mt-2 w-full px-4 py-3 text-sm"
            placeholder="priya@example.com"
            autoComplete="email"
          />
          {errors.emailAddress && <p className="mt-1 text-xs text-red-400">{errors.emailAddress}</p>}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Project of Interest *</label>
          <Select
            value={form.projectOfInterest}
            onValueChange={(v) => setForm((p) => ({ ...p, projectOfInterest: v }))}
          >
            <SelectTrigger
              data-testid="contact-form-project-select"
              className="input-luxe mt-2 w-full px-4 py-3 text-sm h-auto border-white/10 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border border-white/12 text-white">
              {PROJECTS.map((p) => (
                <SelectItem key={p} value={p} className="focus:bg-white/8 focus:text-[color:var(--sp-gold)]">
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.projectOfInterest && <p className="mt-1 text-xs text-red-400">{errors.projectOfInterest}</p>}
        </div>
      </div>
      <div className="mt-5">
        <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Message (optional)</label>
        <textarea
          data-testid="contact-form-message-input"
          value={form.message}
          onChange={onChange("message")}
          rows={4}
          className="input-luxe mt-2 w-full px-4 py-3 text-sm resize-y"
          placeholder="Share your investment goals, preferred plot size, timeline, or any specific question."
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="contact-form-submit-button"
        className="btn-gold mt-7 inline-flex items-center justify-center gap-2 px-7 py-4 text-[12px] tracking-[0.22em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            <Send className="h-4 w-4" /> Send Enquiry
          </>
        )}
      </button>

      {submitted && (
        <div
          data-testid="contact-form-success"
          className="mt-6 flex items-start gap-3 rounded-xl border border-[color:rgba(212,175,55,0.35)] bg-[color:var(--sp-gold-soft)] px-5 py-4"
        >
          <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--sp-gold)] text-black flex-shrink-0">
            <Send className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-sm text-white font-medium">
              Thank you. Your enquiry has been received.
            </p>
            <p className="text-xs text-white/65 mt-1 leading-relaxed">
              Our advisory team will reach out within 24 business hours on the
              email and phone number you provided.
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

const ContactPage = () => {
  const settings = useSettings();
  const contact = settings?.contact || {};
  const seo = getPageSeo("contact");
  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
      {/* HEADER */}
      <section data-testid="contact-hero" className="relative pt-40 pb-16">
        <div className="absolute inset-0 hero-overlay opacity-80" />
        <div className="container-narrow relative">
          <motion.p {...fadeUp} className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> Contact
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl"
          >
            Let’s Build Your <span className="italic text-white/85">Dream</span> Together.
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed"
          >
            Connect with our advisory desk for site visits, private investor
            briefings, or bespoke plot consultations.
          </motion.p>
        </div>
      </section>

      {/* TWO COLUMN INFO + FORM */}
      <section data-testid="contact-main" className="pb-20">
        <div className="container-narrow grid lg:grid-cols-2 gap-10 lg:gap-14">
          <motion.div {...fadeUp} className="space-y-8">
            <div className="glass-card p-7 md:p-9">
              <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                <span className="gold-divider" /> Corporate Information
              </p>
              <ul className="mt-6 divide-y divide-white/8">
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <Phone className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Phone & WhatsApp</p>
                    <a
                      href={`tel:${contact.whatsappDigits || "919637241414"}`}
                      data-testid="contact-info-phone"
                      className="mt-1 block text-white text-base sm:text-lg hover:text-[color:var(--sp-gold)] transition-colors break-all"
                    >
                      {contact.whatsapp || "+91 96372 41414"}
                    </a>
                  </div>
                </li>
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <Globe className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Website</p>
                    <p className="mt-1 text-white text-base sm:text-lg break-all">{contact.website || "www.seetaiproperties.com"}</p>
                  </div>
                </li>
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <Mail className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Email</p>
                    <a
                      href={`mailto:${contact.email || "info@seetaiproperties.com"}`}
                      data-testid="contact-info-email"
                      className="mt-1 block text-white text-base sm:text-lg hover:text-[color:var(--sp-gold)] transition-colors break-all"
                    >
                      {contact.email || "info@seetaiproperties.com"}
                    </a>
                  </div>
                </li>
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <MapPin className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Headquarters</p>
                    <p className="mt-1 text-white text-base sm:text-lg">{contact.headquarters || "Pune & Pandharpur, Maharashtra, India"}</p>
                  </div>
                </li>
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <MapPin className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Site Address</p>
                    <p className="mt-1 text-white text-sm sm:text-base leading-relaxed">
                      {contact.siteAddress || "Airport Link Road, Bhandgaon Jejuri Road, Khor, Maharashtra 412214"}
                    </p>
                  </div>
                </li>
                <li className="py-5 flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30">
                    <Clock className="h-4 w-4 text-[color:var(--sp-gold)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Office Hours</p>
                    <p className="mt-1 text-white text-sm sm:text-base">{contact.officeHours || "Mon – Sat · 10:00 AM – 7:00 PM IST"}</p>
                  </div>
                </li>
              </ul>
              <a
                href={`https://wa.me/${contact.whatsappDigits || "919637241414"}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                data-testid="contact-info-whatsapp-cta"
                className="btn-ghost neon-button mt-2 inline-flex items-center gap-2 px-6 py-3 text-[12px] tracking-[0.22em] uppercase"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <ContactForm />
          </motion.div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="contact-map" className="pb-24">
        <div className="container-narrow">
          <div className="glass-card overflow-hidden">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-5 border-b border-white/10">
              <div>
                <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                  <span className="gold-divider" /> Site Location
                </p>
                <p className="mt-1 text-white text-sm md:text-base">
                  {contact.siteAddress ||
                    "Airport Link Road, Jejuri Road, Bhandgaon, Maharashtra 412214"}
                </p>
                <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-white/55">
                  18°24&apos;45.0&quot;N · 74°19&apos;31.1&quot;E · 66 Acres
                </p>
              </div>
              <a
                href="https://www.google.com/maps?q=18.4125,74.325306&z=15"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-map-directions"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:rgba(212,175,55,0.5)] bg-[color:var(--sp-gold-soft)] hover:bg-[color:var(--sp-gold)] hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgba(212,175,55,0.6)] px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold whitespace-nowrap text-[color:var(--sp-gold)] transition-colors self-start sm:self-auto no-gold-shine"
              >
                Get Directions <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>

            <SilvaCityProjectMap />
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
